// Abgaben an Kunden: Liste, Videos hochladen (in Stücken), Link und Code teilen, Feedback und Aktivität sehen.
import type { Client, Project } from "../shared/types.js";
import { ARCHIVE_DAYS, PHOTO_VIEW_PX, PLATFORMS, fmtBytes, fmtTime, type DeliveryKind, type Platform, type DeliveryCommentRow, type DeliveryEventRow, type DeliveryFull, type DeliveryListItem, type DeliveryVideoRow } from "../shared/delivery.js";
import { ApiError, api } from "./api.js";
import { ic } from "./icons.js";
import { esc, fmtDate } from "./util.js";

export interface DeliveryHost {
  clients: () => Client[];
  projects: () => Project[];
  ownerName: () => string;
  go: (to: string) => void;
  rerender: () => void;
  toast: (msg: string) => void;
  openModal: (html: string, cls?: string) => void;
  closeModal: () => void;
  confirm: (text: string, label: string, fn: () => void | Promise<void>) => void;
  mhead: (t: string) => string;
  pageHead: (eyebrow: string, title: string, lede: string, acts?: string) => string;
  emptyBox: (title: string, text: string, btn?: string) => string;
  authLost: (e: unknown) => boolean;
}
let H: DeliveryHost;
export function initDeliveries(host: DeliveryHost) {
  H = host;
}

let LIST: DeliveryListItem[] | null | undefined;
/** Liste zeigt das Archiv statt der aktiven Abgaben */
let ARCH = false;
let ARCH_COUNT = 0;
let CUR: { id: string; d: DeliveryFull | null; err: string } | null = null;

interface Upload {
  key: string;
  deliveryId: string;
  name: string;
  size: number;
  sent: number;
  replaceId?: string;
  state: "prep" | "up" | "done" | "error";
  error: string;
  cancel: boolean;
  /** vom Server vergeben, sobald der Upload begonnen hat */
  videoId?: string;
}
const UPLOADS: Upload[] = [];

const failMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Unbekannter Fehler");
const clientName = (id: string | null) => (id ? (H.clients().find((c) => c.id === id)?.name ?? "") : "");
const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.round(m / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.round(h / 24);
  return d < 8 ? `vor ${d} Tag${d === 1 ? "" : "en"}` : fmtDate(iso);
};
const ratio = (v: DeliveryVideoRow) => {
  if (!v.width || !v.height) return "";
  const r = v.width / v.height;
  const known: [number, string][] = [
    [9 / 16, "9:16"],
    [4 / 5, "4:5"],
    [1, "1:1"],
    [16 / 9, "16:9"],
  ];
  return known.reduce((a, b) => (Math.abs(b[0] - r) < Math.abs(a[0] - r) ? b : a))[1];
};
const onDetail = () => location.hash.startsWith("#abgabe-");

/* ============ Liste ============ */

async function loadList() {
  LIST = null;
  try {
    const r = await api.deliveries(undefined, ARCH);
    LIST = r.deliveries;
    ARCH_COUNT = r.archivedCount;
  } catch (e) {
    if (H.authLost(e)) return;
    LIST = [];
    H.toast(failMsg(e));
  }
  if (location.hash.startsWith("#abgaben")) H.rerender();
}

const daysLeft = (iso: string) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
/** Hinweis bei archivierten Abgaben: wann sie endgültig verschwinden */
const purgeText = (d: DeliveryListItem) => {
  if (!d.purgeAt) return "";
  const n = daysLeft(d.purgeAt);
  return n <= 0 ? "Wird demnächst endgültig gelöscht" : `Wird am ${fmtDate(d.purgeAt)} endgültig gelöscht · noch ${n} ${n === 1 ? "Tag" : "Tage"}`;
};

/** «Vorschau»: Einblick in einen Zwischenstand (v1, Sample) – schlichte Kundenseite, Feedback statt Freigabe */
const previewChip = `<span class="chip dv-pv">${ic("eye", 12)} Vorschau</span>`;
const photosChip = `<span class="chip dv-pv">${ic("image", 12)} Fotos</span>`;
const kindChip = (d: DeliveryListItem) => (d.kind === "preview" ? previewChip : d.kind === "photos" ? photosChip : "");
const countText = (d: Pick<DeliveryListItem, "kind" | "videos">) => (d.kind === "photos" ? `${d.videos} Foto${d.videos === 1 ? "" : "s"}` : `${d.videos} Video${d.videos === 1 ? "" : "s"}`);
function statusChips(d: DeliveryListItem) {
  const chips: string[] = kindChip(d) ? [kindChip(d)] : [];
  const asked = d.request ? `<span class="chip warn">${ic("bell", 12)} Freigabe angefragt</span>` : "";
  if (d.archivedAt) return `${kindChip(d)}${asked}<span class="chip">${ic("archive", 12)} Archiviert</span>`;
  if (asked) chips.push(asked);
  if (!d.active) chips.push('<span class="chip">Gesperrt</span>');
  else if (d.expired) chips.push('<span class="chip bad">Abgelaufen</span>');
  if (d.kind === "final" && d.videos && d.approved === d.videos) chips.push(`<span class="chip ok">${ic("check", 12)} Freigegeben</span>`);
  else if (d.kind === "final" && d.approved) chips.push(`<span class="chip ok">${d.approved}/${d.videos} freigegeben</span>`);
  if (d.openComments) chips.push(`<span class="chip warn">${d.openComments} Feedback</span>`);
  chips.push(d.lastOpenedAt ? `<span class="chip acc">Geöffnet ${ago(d.lastOpenedAt)}</span>` : '<span class="chip">Noch nicht geöffnet</span>');
  return chips.join("");
}

export function vAbgaben(): string {
  if (LIST === undefined) void loadList();
  const head = H.pageHead(
    "Arbeit",
    "Abgaben",
    "Fertige Videos übergeben: ein Link mit Code, schön präsentiert, mit Freigabe. Für Zwischenstände (v1, Samples) gibt es die schlichte Vorschau.",
    `<button class="btn" data-d="new-photos">${ic("image", 16)}Neue Fotos</button><button class="btn" data-d="new-preview">${ic("eye", 16)}Neue Vorschau</button><button class="btn pri" data-d="new">${ic("plus", 16)}Neue Abgabe</button>`,
  );
  const tabs = `<div class="dv-listbar"><div class="seg"><button class="${ARCH ? "" : "on"}" data-d="lmode" data-k="">Aktiv</button><button class="${ARCH ? "on" : ""}" data-d="lmode" data-k="arch">${ic("archive", 13)} Archiv${ARCH_COUNT ? `<span class="dim">${ARCH_COUNT}</span>` : ""}</button></div>${ARCH ? `<span class="muted small">Archivierte Abgaben sind für Kunden nicht mehr erreichbar und werden nach ${ARCHIVE_DAYS} Tagen endgültig gelöscht, auch in der Nextcloud.</span>` : ""}</div>`;
  if (!LIST) return `${head}${tabs}<div class="loading" style="padding:50px 0">Abgaben werden geladen …</div>`;
  if (!LIST.length)
    return `${head}${tabs}${
      ARCH
        ? H.emptyBox("Archiv ist leer", "Archivierte Abgaben erscheinen hier und werden nach 30 Tagen automatisch gelöscht.", "")
        : H.emptyBox("Noch keine Abgaben", "Lege eine Abgabe an, lade die fertigen Videos hoch und schick dem Kunden den Link mit Code.", `<button class="btn pri sm" data-d="new">${ic("plus", 15)}Erste Abgabe</button>`)
    }`;
  return `${head}${tabs}<div class="dv-grid">${LIST.map(
    (d) => `<a class="dv-card ${d.archivedAt ? "arch" : ""}" href="#abgabe-${esc(d.id)}">
      <span class="dv-thumb">${d.poster ? `<img src="${esc(d.poster)}" alt="" loading="lazy">` : `<span class="dv-ph">${ic(d.kind === "photos" ? "image" : "film", 26)}</span>`}<span class="dv-count">${countText(d)}</span>${
        d.archivedAt ? "" : `<button class="dv-cardact" data-d="arch" data-id="${esc(d.id)}" title="Archivieren" aria-label="Archivieren">${ic("archive", 16)}</button>`
      }</span>
      <span class="dv-body"><b class="dv-title">${esc(d.title)}</b><span class="muted small">${esc(clientName(d.clientId) || "Ohne Kunde")} · ${fmtDate(d.createdAt)}</span>${
        d.archivedAt
          ? `${d.request ? `<span class="dv-chips"><span class="chip warn">${ic("bell", 12)} Freigabe angefragt</span></span>` : ""}<span class="dv-purge">${ic("clock", 13)} ${purgeText(d)}</span><span class="dv-archacts"><button class="btn sm" data-d="unarch" data-id="${esc(d.id)}">${ic("unarchive", 14)} Zurückholen</button><button class="btn sm ghost dng" data-d="purge" data-id="${esc(d.id)}" data-t="${esc(d.title)}">${ic("trash", 14)} Jetzt löschen</button></span>`
          : `<span class="dv-chips">${statusChips(d)}</span>`
      }</span>
    </a>`,
  ).join("")}</div>`;
}

function projectOptions(clientId: string, sel: string) {
  const ps = H.projects().filter((p) => (!clientId || p.client === clientId) && !["Verrechnet", "Abgeschlossen"].includes(p.phase));
  return `<option value="">Kein Projekt</option>${ps.map((p) => `<option value="${esc(p.id)}" ${p.id === sel ? "selected" : ""}>${esc(p.title)}</option>`).join("")}`;
}

export function openNewDelivery(clientId = "", kind: DeliveryKind = "final") {
  const clients = H.clients().filter((c) => c.status !== "archiv");
  const kindOpt = (k: DeliveryKind, icon: string, title: string, text: string) =>
    `<label class="dv-kind"><input type="radio" name="dv-n-kind" value="${k}" ${k === kind ? "checked" : ""}><span class="lic">${ic(icon, 16)}</span><span><b>${title}</b><small>${text}</small></span></label>`;
  H.openModal(
    `${H.mhead("Neu teilen")}<form data-dform="new" class="stack" style="gap:14px">
      <div class="dv-kinds">${kindOpt("final", "send", "Abgabe", "Fertige Videos, schön präsentiert. Der Kunde gibt frei und lädt herunter.")}${kindOpt("preview", "eye", "Vorschau", "Einblick in einen Zwischenstand (v1, Sample). Schlicht, mit Feedback, ohne Freigabe.")}${kindOpt("photos", "image", "Fotos", "Bilder als schöne Galerie. Der Kunde schaut sie an und lädt sie einfach herunter.")}</div>
      <div class="field"><label for="dv-n-title">Titel</label><input class="in" id="dv-n-title" placeholder="z. B. Reels Herbstkampagne" maxlength="200" autocomplete="off" required></div>
      <div class="form-grid"><div class="field"><label for="dv-n-client">Kunde</label><select class="in" id="dv-n-client" data-d="n-client"><option value="">Ohne Kunde</option>${clients.map((c) => `<option value="${esc(c.id)}" ${c.id === clientId ? "selected" : ""}>${esc(c.name)}</option>`).join("")}</select></div>
      <div class="field"><label for="dv-n-project">Projekt (optional)</label><select class="in" id="dv-n-project">${projectOptions(clientId, "")}</select></div></div>
      <div class="field"><label for="dv-n-msg">Nachricht an den Kunden (optional)</label><textarea class="in" id="dv-n-msg" rows="3" maxlength="3000" placeholder="Erscheint auf der Seite, z. B. «Hier sind eure Reels für den Herbst.» Bei einer Vorschau: «Erster Schnitt, Farben und Musik folgen noch.»"></textarea></div>
      <label class="toggle"><input type="checkbox" id="dv-n-code" checked><span><b>Mit Code schützen</b><small>Der Kunde braucht zusätzlich zum Link einen 6-stelligen Code.</small></span></label>
      <div class="modal-foot"><button type="button" class="btn ghost" data-a="close">Abbrechen</button><button class="btn pri" type="submit">Erstellen</button></div></form>`,
  );
}

/* ============ Detail ============ */

async function loadDetail(id: string) {
  try {
    const { delivery } = await api.delivery(id);
    if (CUR?.id !== id) return;
    CUR.d = delivery;
    CUR.err = "";
  } catch (e) {
    if (H.authLost(e) || CUR?.id !== id) return;
    CUR.err = failMsg(e);
  }
  if (location.hash === `#abgabe-${id}`) paint();
}

function paint() {
  const v = document.getElementById("view");
  if (!v || !CUR || !onDetail()) return;
  const y = window.scrollY;
  v.innerHTML = vAbgabe(CUR.id);
  window.scrollTo(0, y);
}

export function vAbgabe(id: string): string {
  if (!CUR || CUR.id !== id) {
    CUR = { id, d: null, err: "" };
    void loadDetail(id);
  }
  const back = `<a href="#abgaben" class="sk-back">${ic("left", 16)}Alle Abgaben</a>`;
  if (CUR.err) return `${back}${H.emptyBox("Abgabe nicht gefunden", esc(CUR.err), `<a class="btn sm" href="#abgaben">Zur Liste</a>`)}`;
  if (!CUR.d) return `${back}<div class="loading" style="padding:50px 0">Abgabe wird geladen …</div>`;
  return detailHTML(CUR.d);
}

const eventText = (e: DeliveryEventRow, d: DeliveryFull) => {
  const v = d.videoRows.find((x) => x.id === e.videoId)?.title ?? e.detail;
  switch (e.kind) {
    case "open":
      return [ic("ext", 14), "Link geöffnet"];
    case "play":
      return [ic("play", 13), `«${esc(v)}» abgespielt`];
    case "download":
      return [ic("download", 14), `«${esc(v)}» heruntergeladen`];
    case "approve":
      return [ic("check", 14), `Freigegeben: ${esc(e.detail)}`];
    case "comment":
      return [ic("note", 14), `Feedback: ${esc(e.detail)}`];
    case "request":
      return [ic("bell", 14), `Freigabe angefragt: ${esc(e.detail)}`];
  }
};

function shareMessage(d: DeliveryFull) {
  const c = H.clients().find((x) => x.id === d.clientId);
  const hi = c?.contact ? `Hallo ${c.contact.split(" ")[0]}` : "Hallo";
  if (d.kind === "photos") return `${hi}\n\nhier ${d.videos === 1 ? "ist euer Foto" : "sind eure Fotos"} zu «${d.title}»:\n${d.link}${d.code ? `\nCode: ${d.code}` : ""}\n\nIhr könnt sie direkt anschauen und einzeln oder alle zusammen herunterladen.\n\nLiebe Grüsse\n${H.ownerName()}`;
  if (d.kind === "preview")
    return `${hi}\n\nhier ein erster Einblick in den aktuellen Stand von «${d.title}» – noch nicht final:\n${d.link}${d.code ? `\nCode: ${d.code}` : ""}\n\nSchau gerne rein und gib direkt an der passenden Stelle im Video Feedback.\n\nLiebe Grüsse\n${H.ownerName()}`;
  return `${hi}\n\nhier ${d.videos === 1 ? "ist euer Video" : "sind eure Videos"} zu «${d.title}»:\n${d.link}${d.code ? `\nCode: ${d.code}` : ""}\n\nIhr könnt direkt im Browser anschauen, herunterladen und freigeben oder an einer bestimmten Stelle Feedback geben.\n\nLiebe Grüsse\n${H.ownerName()}`;
}

/** Gewählter Reiter im Beitragstext pro Video (bleibt beim Neuzeichnen) */
const capTab = new Map<string, "all" | Platform>();

/** Beitragstext: ein gemeinsamer Text und auf Wunsch ein eigener pro Plattform, als Reiter */
function captionBox(v: DeliveryVideoRow) {
  const plats = (Object.keys(PLATFORMS) as Platform[]).filter((p) => v.platforms.includes(p));
  if (!plats.length) return `<div class="dv-cap empty"><span class="dv-caph">Beitragstext</span><p>Wähle links eine Plattform, dann schreibst du hier den Text für die Vorschau.</p></div>`;
  const multi = plats.length > 1;
  let cur = capTab.get(v.id) ?? "all";
  if (cur !== "all" && !plats.includes(cur)) cur = "all";
  const area = (k: "all" | Platform) =>
    `<textarea class="in" rows="5" maxlength="2200" data-id="${esc(v.id)}" ${k === "all" ? 'data-d="caption"' : `data-d="pcap" data-k="${k}"`} data-pane="${k}" ${k === cur ? "" : "hidden"} placeholder="${k === "all" ? "Der geplante Post-Text, erscheint in der Vorschau unter dem Video" : `Eigener Text für ${PLATFORMS[k].label}. Leer = Text von «Für alle»`}">${esc(k === "all" ? v.caption : (v.captions[k] ?? ""))}</textarea>`;
  const tab = (k: "all" | Platform) =>
    `<button class="dv-ctab ${k === cur ? "on" : ""} ${(k === "all" ? v.caption : v.captions[k]) ? "has" : ""}" data-d="captab" data-id="${esc(v.id)}" data-k="${k}" role="tab" aria-selected="${k === cur}">${k === "all" ? "Für alle" : PLATFORMS[k].label}</button>`;
  return `<div class="dv-cap">
    <div class="dv-caphead"><span class="dv-caph">Beitragstext${multi ? "" : ` · ${PLATFORMS[plats[0]!].label}`}</span>${multi ? `<div class="dv-ctabs" role="tablist">${tab("all")}${plats.map(tab).join("")}</div>` : ""}</div>
    ${area("all")}${multi ? plats.map(area).join("") : ""}
  </div>`;
}

function detailHTML(d: DeliveryFull): string {
  const clients = H.clients();
  const ups = UPLOADS.filter((u) => u.deliveryId === d.id && u.state !== "done");
  const commentsBy = (vid: string) => d.comments.filter((c) => c.videoId === vid);
  const expires = d.expiresAt ? d.expiresAt.slice(0, 10) : "";
  const head = `<div class="dv-head">
    <a href="#abgaben" class="sk-back">${ic("left", 16)}Alle Abgaben</a>
    <input class="sk-title" data-d="f" data-k="title" value="${esc(d.title)}" maxlength="200" aria-label="Titel">
    <div class="sk-metarow">
      <label class="pill-sel ghost">${ic("users", 13)}<select data-d="client" aria-label="Kunde"><option value="">Ohne Kunde</option>${clients.map((c) => `<option value="${esc(c.id)}" ${c.id === d.clientId ? "selected" : ""}>${esc(c.name)}</option>`).join("")}</select>${ic("chevd", 13)}</label>
      <label class="pill-sel ghost">${ic("board", 13)}<select data-d="project" aria-label="Projekt">${projectOptions(d.clientId ?? "", d.projectId ?? "")}</select>${ic("chevd", 13)}</label>
      ${statusChips(d)}
    </div>${
      d.request
        ? `<div class="dv-reqbar">${ic("bell", 18)}<div class="min0"><b>${esc(d.request.name)} möchte die Abgabe nochmals sehen</b><small>${[ago(d.request.at), d.request.contact ? esc(d.request.contact) : ""].filter(Boolean).join(" · ")}</small>${d.request.message ? `<p>«${esc(d.request.message)}»</p>` : ""}</div><div class="dv-reqacts"><button class="btn pri sm" data-d="reopen" data-id="${esc(d.id)}">${ic("check", 14)} Wieder freigeben</button><button class="btn ghost sm" data-d="dismissreq" data-id="${esc(d.id)}">Erledigt</button></div></div>`
        : ""
    }${
      d.archivedAt
        ? `<div class="dv-archbar">${ic("archive", 16)}<span><b>Archiviert.</b> Der Link ist für den Kunden aus. ${esc(purgeText(d))}, auch in der Nextcloud.</span><button class="btn sm" data-d="unarch" data-id="${esc(d.id)}">${ic("unarchive", 14)} Zurückholen</button></div>`
        : ""
    }</div>`;

  const videos = d.videoRows.filter((v) => v.status === "ready");
  const busy = UPLOADS.some((u) => u.deliveryId === d.id && (u.state === "up" || u.state === "prep"));
  const pending = d.videoRows.filter((v) => v.status === "transfer" || v.status === "error" || (v.status === "uploading" && !busy));
  const videoList = videos.length
    ? videos
        .map((v, i) => {
          const cs = commentsBy(v.id);
          const open = cs.filter((c) => !c.done).length;
          return `<div class="dv-video">
          <button class="dv-vthumb ${v.height > v.width ? "tall" : ""}" data-d="play" data-id="${esc(v.id)}" aria-label="Abspielen">${v.poster ? `<img src="${esc(v.poster)}" alt="">` : `<span class="dv-ph">${ic("film", 22)}</span>`}<span class="dv-playic">${ic("play", 18)}</span><span class="dv-dur">${fmtTime(v.duration)}</span></button>
          <div class="dv-vbody">
            <input class="dv-vtitle" data-d="vtitle" data-id="${esc(v.id)}" value="${esc(v.title)}" maxlength="200" aria-label="Titel des Videos">
            <span class="muted small">${[ratio(v), fmtBytes(v.size), v.version > 1 ? `Version ${v.version}` : ""].filter(Boolean).join(" · ")}</span>
            ${
              d.kind === "preview"
                ? ""
                : `<div class="dv-plat"><span class="dim small">Vorschau für den Kunden</span>${(Object.entries(PLATFORMS) as [Platform, { label: string; vertical: boolean }][])
              .filter(([, p]) => !p.vertical || v.height > v.width)
              .map(([k, p]) => `<button class="dv-pchip ${v.platforms.includes(k) ? "on" : ""}" data-d="plat" data-id="${esc(v.id)}" data-k="${k}">${v.platforms.includes(k) ? ic("check", 12) : ic("plus", 12)} ${p.label}</button>`)
              .join("")}</div>`
            }
            <span class="dv-vstate">${d.kind === "preview" ? `<span class="chip">Entwurf · V${v.version}</span>` : v.approvedAt ? `<span class="chip ok">${ic("check", 12)} Freigegeben von ${esc(v.approvedBy)} · ${fmtDate(v.approvedAt)}</span>` : '<span class="chip">Noch nicht freigegeben</span>'}${open ? `<span class="chip warn">${open} offenes Feedback</span>` : cs.length ? `<span class="chip">${cs.length} Feedback erledigt</span>` : ""}</span>
          </div>
          <div class="dv-vacts">
            <button type="button" class="btn sm" data-pick="replace" data-id="${esc(v.id)}" title="Überarbeitete Fassung hochladen: Link bleibt gleich, Freigabe beginnt neu">${ic("refresh", 14)} Neue Version</button>
            <button class="ibtn sm" data-d="vup" data-id="${esc(v.id)}" title="Nach oben" ${i === 0 ? "disabled" : ""}>${ic("up", 15)}</button>
            <button class="ibtn sm" data-d="vdown" data-id="${esc(v.id)}" title="Nach unten" ${i === videos.length - 1 ? "disabled" : ""}>${ic("down", 15)}</button>
            <button class="ibtn sm" data-d="vdel" data-id="${esc(v.id)}" title="Video entfernen">${ic("trash", 15)}</button>
          </div>
          ${d.kind === "preview" ? "" : captionBox(v)}</div>`;
        })
        .join("")
    : "";
  const photos = d.kind === "photos";
  const photoGrid = photos && videos.length
    ? `<div class="dv-photos">${videos
        .map(
          (v, i) => `<div class="dv-photo ${i === 0 ? "first" : ""}"><button class="dv-pimg" data-d="photo" data-id="${esc(v.id)}" aria-label="${esc(v.title)} ansehen">${v.poster ? `<img src="${esc(v.poster)}" alt="" loading="lazy">` : `<span class="dv-ph">${ic("image", 22)}</span>`}</button>${i === 0 ? `<span class="dv-pfirst">Titelbild</span>` : ""}<span class="dv-pacts"><button class="ibtn sm" data-d="vup" data-id="${esc(v.id)}" title="Nach vorne" ${i === 0 ? "disabled" : ""}>${ic("left", 14)}</button><button class="ibtn sm" data-d="vdown" data-id="${esc(v.id)}" title="Nach hinten" ${i === videos.length - 1 ? "disabled" : ""}><span style="display:inline-flex;transform:rotate(180deg)">${ic("left", 14)}</span></button><button class="ibtn sm" data-d="vdel" data-id="${esc(v.id)}" title="Foto entfernen">${ic("trash", 14)}</button></span></div>`,
        )
        .join("")}</div>`
    : "";
  const waiting = ups.filter((u) => u.state !== "done");
  const shownUps = photos ? waiting.slice(0, 4) : waiting;
  const upload = `<button type="button" class="dv-drop" id="dv-drop" data-pick="files">${ic("download", 22)}<b>${photos ? "Fotos hierher ziehen" : "Videos hierher ziehen"}</b><span>oder klicken zum Auswählen · ${photos ? "mehrere auf einmal · JPEG, PNG, WEBP, HEIC" : "MP4, MOV, WEBM"}</span></button>
    <div id="dv-ups">${shownUps.map(upHTML).join("")}${waiting.length > shownUps.length ? `<p class="muted small" style="margin:6px 0 0">… und ${waiting.length - shownUps.length} weitere</p>` : ""}${pending
      .map((v) =>
        v.status === "transfer"
          ? `<div class="dv-up"><span class="ellip">${esc(v.fileName)}</span><span class="small">Wird in die Nextcloud übertragen …</span><span class="dv-bar run"><i></i></span></div>`
          : v.status === "error"
            ? `<div class="dv-up err"><span class="ellip">${esc(v.fileName)}</span><span class="small">${esc(v.error || "Übertragung fehlgeschlagen")}</span><span class="dv-upacts"><button class="btn sm" data-d="retry" data-id="${esc(v.id)}">${ic("refresh", 13)} Nochmals</button><button class="ibtn sm" data-d="vdel" data-id="${esc(v.id)}" title="Entfernen">${ic("x", 14)}</button></span></div>`
            : `<div class="dv-up err"><span class="ellip">${esc(v.fileName)}</span><span class="small">Upload abgebrochen</span><button class="ibtn sm" data-d="vdel" data-id="${esc(v.id)}" title="Entfernen">${ic("x", 14)}</button></div>`,
      )
      .join("")}</div>
    ${d.folder ? `<p class="help" style="margin:10px 0 0">${ic("cloud", 13)} Gespeichert in der Nextcloud: <b>${esc(d.folder)}</b></p>` : ""}`;

  const feedback = d.comments.length
    ? `<section class="panel"><div class="panel-head"><h2 class="h2">Feedback</h2><span class="chip ${d.openComments ? "warn" : ""}">${d.openComments} offen</span></div><div class="dv-fb">${videos
        .filter((v) => commentsBy(v.id).length)
        .map(
          (v) => `<div class="dv-fbv"><span class="flabel">${esc(v.title)}</span>${commentsBy(v.id)
            .map(
              (c: DeliveryCommentRow) => `<div class="dv-c ${c.done ? "done" : ""}">${c.at != null ? `<button class="dv-at" data-d="play" data-id="${esc(v.id)}" data-at="${c.at}">${fmtTime(c.at)}</button>` : '<span class="dv-at gen">Allg.</span>'}<div class="min0"><b>${esc(c.author)}</b> <span class="dim small">${ago(c.createdAt)}${c.version < v.version ? ` · zu Version ${c.version}` : ""}</span><p>${esc(c.text).replace(/\n/g, "<br>")}</p></div>
              <button class="btn sm ${c.done ? "" : "okb"}" data-d="cdone" data-id="${esc(c.id)}" data-k="${c.done ? "0" : "1"}">${c.done ? "Wieder öffnen" : `${ic("check", 13)} Erledigt`}</button><button class="ibtn sm" data-d="cdel" data-id="${esc(c.id)}" title="Löschen">${ic("trash", 14)}</button></div>`,
            )
            .join("")}</div>`,
        )
        .join("")}</div></section>`
    : "";

  const share = `<div class="dv-share">
    <div class="dv-sharehead"><span class="flabel">Teilen</span><label class="dv-switch" title="Link aktiv"><input type="checkbox" data-d="active" ${d.active ? "checked" : ""}><span></span>${d.active ? "Aktiv" : "Gesperrt"}</label></div>
    <div class="dv-link"><input class="in sm" readonly value="${esc(d.link)}" aria-label="Link"><button class="ibtn outline sm" data-d="copy" data-v="${esc(d.link)}" title="Link kopieren">${ic("copy", 15)}</button><a class="ibtn outline sm" href="${esc(d.link)}" target="_blank" rel="noopener" title="Wie der Kunde ansehen">${ic("ext", 15)}</a></div>
    ${d.code ? `<div class="dv-code"><span class="flabel">Code</span><b class="tnum">${esc(d.code.slice(0, 3))} ${esc(d.code.slice(3))}</b><button class="ibtn sm" data-d="copy" data-v="${esc(d.code)}" title="Code kopieren">${ic("copy", 15)}</button></div>` : `<p class="muted small" style="margin:0">Ohne Code: Jeder mit dem Link kann die ${d.kind === "photos" ? "Fotos" : "Videos"} sehen.</p>`}
    <button class="btn pri block" data-d="copymsg">${ic("send", 15)} Nachricht mit Link und Code kopieren</button>
    <div class="dv-codeacts">${d.code ? `<button class="sk-link" data-d="newcode">${ic("refresh", 13)}Neuer Code</button><button class="sk-link" data-d="setcode">${ic("edit", 13)}Code festlegen</button><button class="sk-link" data-d="nocode">${ic("x", 13)}Ohne Code</button>` : `<button class="sk-link" data-d="newcode">${ic("lock", 13)}Code verlangen</button><button class="sk-link" data-d="setcode">${ic("edit", 13)}Eigenen Code</button>`}</div>
    ${
      d.prevCode !== null
        ? `<div class="dv-prevcode">${ic("clock", 13)}<span>Vorher: ${d.prevCode ? `<b class="tnum">${esc(d.prevCode.slice(0, 3))} ${esc(d.prevCode.slice(3))}</b>` : "ohne Code"}</span><button class="sk-link" data-d="restorecode">Zurückholen</button></div>`
        : ""
    }
  </div>
  <div class="dv-block"><span class="flabel">Text für den Kunden</span><textarea class="in" rows="4" maxlength="3000" data-d="f" data-k="message" placeholder="Erscheint gross auf der Seite des Kunden">${esc(d.message)}</textarea></div>
  <div class="dv-block"><span class="flabel">Einstellungen</span>
    ${d.kind === "photos" ? "" : `<label class="dv-opt"><span>Art</span><select class="in sm" data-d="kind" style="width:auto"><option value="final" ${d.kind === "final" ? "selected" : ""}>Abgabe (fertig, mit Freigabe)</option><option value="preview" ${d.kind === "preview" ? "selected" : ""}>Vorschau (Zwischenstand, schlicht)</option></select></label>`}
    <label class="dv-opt"><input type="checkbox" data-d="dl" ${d.allowDownload ? "checked" : ""}> Herunterladen erlauben <span class="dim">${d.kind === "final" ? "(nach der Freigabe)" : "(sofort)"}</span></label>
    ${d.kind !== "final" ? "" : `<label class="dv-opt" title="Zeigt dem Kunden in der Instagram- und TikTok-Ansicht, wo Texte und Knöpfe der App liegen"><input type="checkbox" data-d="sz" ${d.showSafeZone ? "checked" : ""}> Sicherheitsbereich für den Kunden einblendbar</label>`}
    <label class="dv-opt">Gültig bis <input class="in sm" type="date" data-d="expires" value="${esc(expires)}"></label>
  </div>
  <div class="dv-block"><span class="flabel">Aktivität</span>${
    d.events.length
      ? `<ul class="dv-ev">${d.events
          .slice(0, 30)
          .map((e) => {
            const [icon, text] = eventText(e, d);
            return `<li><span class="dv-evic ${e.kind}">${icon}</span><span class="min0">${text}<small>${ago(e.createdAt)}</small></span></li>`;
          })
          .join("")}</ul>`
      : `<p class="muted small" style="margin:0">Noch nichts passiert. Sobald der Kunde den Link öffnet, siehst du es hier und über die Glocke.</p>`
  }</div>
  <div class="dv-block dv-danger"><button class="sk-link" data-d="newtoken">${ic("refresh", 13)}Neuer Link (alter wird ungültig)</button>${
    d.archivedAt ? `<button class="sk-link" data-d="unarch" data-id="${esc(d.id)}">${ic("unarchive", 13)}Zurückholen</button>` : `<button class="sk-link" data-d="arch" data-id="${esc(d.id)}">${ic("archive", 13)}Archivieren</button>`
  }<button class="sk-link dng" data-d="del">${ic("trash", 13)}Endgültig löschen</button></div>`;

  return `${head}<div class="dv-layout"><div class="dv-main"><section class="panel"><div class="panel-head"><h2 class="h2">${photos ? "Fotos" : "Videos"}</h2><span class="muted small">${photos && videos.length ? `${videos.length} · Reihenfolge wie auf der Kundenseite, das erste ist das Titelbild` : videos.length ? "Reihenfolge wie auf der Kundenseite" : ""}</span></div>${photos ? photoGrid : videoList}${upload}</section>${feedback}</div><aside class="dv-side">${share}</aside></div>`;
}

function upHTML(u: Upload) {
  const pct = u.size ? Math.round((u.sent / u.size) * 100) : 0;
  return `<div class="dv-up ${u.state === "error" ? "err" : ""}" id="up-${u.key}"><span class="ellip">${u.replaceId ? "Neue Version: " : ""}${esc(u.name)}</span><span class="small tnum" id="upt-${u.key}">${u.state === "prep" ? "Wird vorbereitet …" : u.state === "error" ? esc(u.error) : `${pct}% · ${fmtBytes(u.sent)} von ${fmtBytes(u.size)}`}</span><span class="dv-bar"><i id="upb-${u.key}" style="width:${pct}%"></i></span>${u.state === "up" || u.state === "prep" ? `<button class="ibtn sm" data-d="cancel" data-id="${u.key}" title="Abbrechen">${ic("x", 14)}</button>` : ""}</div>`;
}

/* ---------- Upload ---------- */

/** Dauer, Grösse und Vorschaubild direkt im Browser aus der Datei lesen (kein ffmpeg nötig). */
function probe(file: File): Promise<{ duration: number; width: number; height: number; poster: Blob | null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.muted = true;
    v.preload = "auto";
    v.playsInline = true;
    let done = false;
    const finish = (poster: Blob | null) => {
      if (done) return;
      done = true;
      const r = { duration: Number.isFinite(v.duration) ? v.duration : 0, width: v.videoWidth, height: v.videoHeight, poster };
      URL.revokeObjectURL(url);
      v.removeAttribute("src");
      v.load();
      resolve(r);
    };
    const timer = setTimeout(() => finish(null), 12000);
    v.addEventListener("loadedmetadata", () => {
      v.currentTime = Math.min(Math.max(0.5, v.duration * 0.15), Math.max(0, v.duration - 0.1));
    });
    v.addEventListener("seeked", () => {
      try {
        const w = Math.min(1280, v.videoWidth || 1280);
        const h = Math.round((w / (v.videoWidth || 16)) * (v.videoHeight || 9));
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        c.getContext("2d")!.drawImage(v, 0, 0, w, h);
        c.toBlob(
          (b) => {
            clearTimeout(timer);
            finish(b);
          },
          "image/jpeg",
          0.84,
        );
      } catch {
        clearTimeout(timer);
        finish(null);
      }
    });
    v.addEventListener("error", () => {
      clearTimeout(timer);
      finish(null);
    });
    v.src = url;
  });
}

/** Foto für die Galerie: Masse lesen und verkleinerte Ansicht erzeugen (das Original bleibt für den Download). */
function probePhoto(file: File): Promise<{ width: number; height: number; poster: Blob | null }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const finish = (r: { width: number; height: number; poster: Blob | null }) => {
      URL.revokeObjectURL(url);
      resolve(r);
    };
    img.onerror = () => finish({ width: 0, height: 0, poster: null });
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      try {
        const k = Math.min(1, PHOTO_VIEW_PX / Math.max(width, height));
        const c = document.createElement("canvas");
        c.width = Math.round(width * k);
        c.height = Math.round(height * k);
        const g = c.getContext("2d")!;
        g.imageSmoothingQuality = "high";
        g.drawImage(img, 0, 0, c.width, c.height);
        c.toBlob((b) => finish({ width, height, poster: b }), "image/jpeg", 0.86);
      } catch {
        finish({ width, height, poster: null });
      }
    };
    img.src = url;
  });
}

function setUp(u: Upload) {
  const el = document.getElementById(`up-${u.key}`);
  if (!el) return;
  el.outerHTML = upHTML(u);
}

async function uploadFiles(deliveryId: string, files: File[], replaceId?: string) {
  const photos = CUR?.d?.kind === "photos";
  // Fotos in der Reihenfolge der Dateinamen (Bild_2 vor Bild_10)
  if (photos) files = [...files].sort((a, b) => a.name.localeCompare(b.name, "de", { numeric: true }));
  const list: [Upload, File][] = files.map((f) => [{ key: Math.random().toString(36).slice(2, 10), deliveryId, name: f.name, size: f.size, sent: 0, replaceId, state: "prep", error: "", cancel: false }, f]);
  for (const [u] of list) UPLOADS.push(u);
  paint();
  if (photos) {
    // Fotos: drei gleichzeitig, sonst dauern grosse Serien lange
    const queue = [...list];
    await Promise.all(
      [0, 1, 2].map(async () => {
        for (let next = queue.shift(); next; next = queue.shift()) {
          await uploadOne(next[0], next[1], true);
          paint();
        }
      }),
    );
    const ok = list.filter(([u]) => u.state === "done");
    if (ok.length) {
      // gleichzeitig hochgeladen: Reihenfolge danach festlegen, neue Fotos hinten anhängen
      const old = (CUR?.d?.videoRows ?? []).filter((v) => v.status === "ready").map((v) => v.id);
      await api.orderDeliveryVideos(deliveryId, [...old, ...ok.map(([u]) => u.videoId ?? "")]).catch(() => undefined);
      H.toast(ok.length === 1 ? "Foto ist hochgeladen" : `${ok.length} Fotos sind hochgeladen`);
    }
  } else for (const [u, f] of list) await uploadOne(u, f);
  if (CUR?.id === deliveryId) await loadDetail(deliveryId);
  LIST = undefined;
}

async function uploadOne(u: Upload, f: File, photo = false) {
  try {
    const mime = photo ? f.type : f.type || (f.name.toLowerCase().endsWith(".mov") ? "video/quicktime" : "video/mp4");
    const meta = photo ? { ...(await probePhoto(f)), duration: 0 } : await probe(f);
    if (photo && !meta.poster) throw new ApiError("bad_file", "Dieses Bild kann der Browser nicht lesen. Bitte als JPEG exportieren.");
    const { video } = await api.startUpload(u.deliveryId, { fileName: f.name, size: f.size, mime, ...(u.replaceId ? { replaceId: u.replaceId } : {}) });
    u.state = "up";
    u.videoId = video.id;
    setUp(u);
    const base = `/api/deliveries/${encodeURIComponent(u.deliveryId)}/videos/${encodeURIComponent(video.id)}`;
    let offset = 0;
    while (offset < f.size) {
      if (u.cancel) throw new ApiError("cancel", "Abgebrochen");
      const part = f.slice(offset, offset + video.chunk);
      let tries = 0;
      for (;;) {
        try {
          const r = await api.putRaw(`${base}/chunk?offset=${offset}`, part, "application/octet-stream");
          offset = r.received ?? offset + part.size;
          break;
        } catch (e) {
          // kurze Netzaussetzer abfangen
          if (++tries >= 4 || (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.code !== "rate_limited")) throw e;
          await new Promise((r) => setTimeout(r, 1500 * tries));
        }
      }
      u.sent = offset;
      const pct = Math.round((offset / f.size) * 100);
      const b = document.getElementById(`upb-${u.key}`);
      const t = document.getElementById(`upt-${u.key}`);
      if (b) b.style.width = `${pct}%`;
      if (t) t.textContent = `${pct}% · ${fmtBytes(offset)} von ${fmtBytes(f.size)}`;
    }
    if (meta.poster) await api.putRaw(`${base}/poster`, meta.poster, "image/jpeg").catch(() => undefined);
    await api.completeUpload(u.deliveryId, video.id, { duration: Math.round(meta.duration * 10) / 10, width: meta.width, height: meta.height });
    u.state = "done";
    if (!photo) H.toast(u.replaceId ? `Neue Version von «${f.name}» ist online` : `«${f.name}» ist hochgeladen`);
  } catch (e) {
    if (H.authLost(e)) return;
    u.state = "error";
    u.error = e instanceof ApiError && e.code === "cancel" ? "Abgebrochen" : failMsg(e);
    setUp(u);
  }
}

/* ---------- Ändern ---------- */

async function save(patch: Parameters<typeof api.saveDelivery>[1], repaint = true) {
  if (!CUR?.d) return;
  try {
    const { delivery } = await api.saveDelivery(CUR.id, patch);
    CUR.d = delivery;
    LIST = undefined;
    if (repaint) paint();
  } catch (e) {
    if (!H.authLost(e)) H.toast(failMsg(e));
  }
}

function playModal(v: DeliveryVideoRow, at?: number) {
  H.openModal(`${H.mhead(esc(v.title))}<video class="dv-modalvid ${v.height > v.width ? "tall" : ""}" src="${esc(v.src)}" ${v.poster ? `poster="${esc(v.poster)}"` : ""} controls autoplay playsinline></video>`, "wide");
  const el = document.querySelector<HTMLVideoElement>(".dv-modalvid");
  if (el && at != null) el.addEventListener("loadedmetadata", () => (el.currentTime = at), { once: true });
}

async function copy(text: string, msg: string) {
  try {
    await navigator.clipboard.writeText(text);
    H.toast(msg);
  } catch {
    H.openModal(`${H.mhead("Zum Kopieren")}<textarea class="in" rows="9" readonly>${esc(text)}</textarea>`);
  }
}

/* ============ Ereignisse ============ */

document.addEventListener("click", (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>("[data-d]");
  if (!el || ["INPUT", "SELECT", "TEXTAREA"].includes(el.tagName) || el.tagName === "LABEL") return;
  const act = el.dataset.d ?? "";
  e.preventDefault();
  const d = CUR?.d;
  const id = el.dataset.id ?? "";
  if (act === "captab") {
    const k = (el.dataset.k ?? "all") as "all" | Platform;
    capTab.set(id, k);
    const box = el.closest(".dv-cap");
    box?.querySelectorAll<HTMLElement>(".dv-ctab").forEach((b) => {
      b.classList.toggle("on", b === el);
      b.setAttribute("aria-selected", String(b === el));
    });
    box?.querySelectorAll<HTMLTextAreaElement>("textarea[data-pane]").forEach((t) => (t.hidden = t.dataset.pane !== k));
    box?.querySelector<HTMLTextAreaElement>(`textarea[data-pane="${k}"]`)?.focus();
    return;
  }
  switch (act) {
    case "new":
      return openNewDelivery();
    case "new-preview":
      return openNewDelivery("", "preview");
    case "new-photos":
      return openNewDelivery("", "photos");
    case "photo": {
      const v = d?.videoRows.find((x) => x.id === id);
      if (v) H.openModal(`${H.mhead(esc(v.title))}<img class="dv-modalimg" src="${esc(v.poster)}" alt="${esc(v.title)}">`, "wide");
      return;
    }
    case "play": {
      const v = d?.videoRows.find((x) => x.id === id);
      if (v) playModal(v, el.dataset.at ? Number(el.dataset.at) : undefined);
      return;
    }
    case "copy":
      return void copy(el.dataset.v ?? "", "Kopiert");
    case "copymsg":
      if (d) void copy(shareMessage(d), "Nachricht kopiert. Jetzt einfach in Mail oder WhatsApp einfügen.");
      return;
    case "cancel": {
      const u = UPLOADS.find((x) => x.key === id);
      if (u) u.cancel = true;
      return;
    }
    case "newcode":
    case "nocode":
      if (!d) return;
      return H.confirm(act === "nocode" ? "Den Code entfernen? Dann kann jeder mit dem Link die Videos sehen." : "Neuen Code erstellen? Wer den alten Code verwendet hat, muss den neuen eingeben.", act === "nocode" ? "Ohne Code" : "Neuer Code", async () => {
        H.closeModal();
        try {
          CUR!.d = (await api.deliveryCode(d.id, { enabled: act === "newcode" })).delivery;
          paint();
          if (CUR!.d.prevCode !== null) H.toast(act === "newcode" ? "Neuer Code erstellt. Der bisherige lässt sich zurückholen." : "Code entfernt. Er lässt sich zurückholen.");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      });
    case "setcode":
      if (!d) return;
      H.openModal(
        `${H.mhead(d.code ? "Code festlegen" : "Eigenen Code")}<form data-dform="code" class="stack" style="gap:14px">
          <p class="muted" style="margin:0">Zum Beispiel den Code, den du dem Kunden schon geschickt hast. Wer den bisherigen Code eingegeben hat, muss danach den neuen eingeben.</p>
          <div class="field"><label for="dv-c-code">Code (6 Ziffern)</label><input class="in dv-codein tnum" id="dv-c-code" inputmode="numeric" autocomplete="off" maxlength="7" pattern="\\d{3}\\s?\\d{3}" placeholder="123 456" required></div>
          <div class="modal-foot"><button type="button" class="btn ghost" data-a="close">Abbrechen</button><button class="btn pri" type="submit">Speichern</button></div></form>`,
      );
      setTimeout(() => document.getElementById("dv-c-code")?.focus(), 30);
      return;
    case "restorecode":
      if (!d) return;
      return void (async () => {
        try {
          CUR!.d = (await api.deliveryCode(d.id, { restore: true })).delivery;
          paint();
          H.toast(CUR!.d.code ? `Code ${CUR!.d.code.slice(0, 3)} ${CUR!.d.code.slice(3)} gilt wieder` : "Wieder ohne Code");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      })();
    case "newtoken":
      if (!d) return;
      return H.confirm("Neuen Link erstellen? Der bisherige Link funktioniert dann sofort nicht mehr.", "Neuer Link", async () => {
        H.closeModal();
        try {
          CUR!.d = (await api.deliveryToken(d.id)).delivery;
          paint();
          H.toast("Neuer Link erstellt");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      });
    case "reopen":
    case "dismissreq":
      return void (async () => {
        try {
          const r = act === "reopen" ? await api.reopenDelivery(id) : await api.dismissDeliveryRequest(id);
          LIST = undefined;
          if (CUR?.id === id) CUR.d = r.delivery;
          // Liste neu zeichnen, wenn sie offen ist (auch wenn die Abgabe vorher schon geöffnet war)
          if (onDetail() && CUR?.id === id) paint();
          else H.rerender();
          H.toast(act === "reopen" ? "Wieder freigegeben. Der Link funktioniert, gib dem Kunden kurz Bescheid." : "Anfrage als erledigt markiert");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      })();
    case "lmode":
      ARCH = el.dataset.k === "arch";
      LIST = undefined;
      H.rerender();
      return;
    case "arch":
    case "unarch":
      return void (async () => {
        const on = act === "arch";
        try {
          const r = await api.archiveDelivery(id, on);
          LIST = undefined;
          if (CUR?.id === id) CUR.d = r.delivery;
          // Liste neu zeichnen, wenn sie offen ist (auch wenn die Abgabe vorher schon geöffnet war)
          if (onDetail() && CUR?.id === id) paint();
          else H.rerender();
          H.toast(on ? `Archiviert. Der Link ist aus, in ${ARCHIVE_DAYS} Tagen wird alles gelöscht.` : "Zurückgeholt. Der Link funktioniert wieder.");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      })();
    case "purge":
      return H.confirm(`«${esc(el.dataset.t ?? "")}» jetzt endgültig löschen? Die Videos werden auch in der Nextcloud gelöscht, samt Papierkorb.`, "Endgültig löschen", async () => {
        H.closeModal();
        try {
          await api.deleteDelivery(id);
          LIST = undefined;
          H.rerender();
          H.toast("Endgültig gelöscht");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      });
    case "del":
      if (!d) return;
      return H.confirm(`«${esc(d.title)}» mit allen Videos und Feedback endgültig löschen? Die Videos werden auch in der Nextcloud gelöscht, samt Papierkorb. Das lässt sich nicht rückgängig machen.`, "Endgültig löschen", async () => {
        H.closeModal();
        try {
          await api.deleteDelivery(d.id);
          CUR = null;
          LIST = undefined;
          H.toast("Abgabe gelöscht");
          H.go("abgaben");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      });
    case "vdel": {
      if (!d) return;
      const v = d.videoRows.find((x) => x.id === id);
      return H.confirm(`«${esc(v?.title ?? (d.kind === "photos" ? "Foto" : "Video"))}» aus der Abgabe entfernen?`, "Entfernen", async () => {
        H.closeModal();
        try {
          await api.deleteDeliveryVideo(d.id, id);
          await loadDetail(d.id);
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      });
    }
    case "vup":
    case "vdown": {
      if (!d) return;
      const ids = d.videoRows.filter((v) => v.status === "ready").map((v) => v.id);
      const i = ids.indexOf(id);
      const j = act === "vup" ? i - 1 : i + 1;
      if (i < 0 || j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j]!, ids[i]!];
      void api.orderDeliveryVideos(d.id, ids).then(() => loadDetail(d.id));
      return;
    }
    case "plat": {
      const v = d?.videoRows.find((x) => x.id === id);
      if (!d || !v) return;
      const k = el.dataset.k as Platform;
      v.platforms = v.platforms.includes(k) ? v.platforms.filter((x) => x !== k) : [...v.platforms, k];
      paint();
      void api.saveDeliveryVideo(d.id, v.id, { platforms: v.platforms }).catch((err: unknown) => H.toast(failMsg(err)));
      return;
    }
    case "retry":
      if (d)
        void api.retryDeliveryVideo(d.id, id).then(
          (r) => {
            CUR!.d = r.delivery;
            paint();
          },
          (err: unknown) => H.toast(failMsg(err)),
        );
      return;
    case "cdone":
      if (d) void api.doneDeliveryComment(d.id, id, el.dataset.k === "1").then(() => loadDetail(d.id));
      return;
    case "cdel":
      if (d) H.confirm("Dieses Feedback löschen?", "Löschen", async () => {
        H.closeModal();
        await api.deleteDeliveryComment(d.id, id).catch(() => undefined);
        await loadDetail(d.id);
      });
      return;
  }
});

document.addEventListener("change", (e) => {
  const el = e.target as HTMLInputElement;
  const act = el.dataset?.d;
  if (!act) return;
  if (act === "n-client") {
    const p = document.getElementById("dv-n-project");
    if (p) p.innerHTML = projectOptions(el.value, "");
    return;
  }
  const d = CUR?.d;
  if (!d) return;
  if (act === "client") void save({ clientId: el.value || null, ...(el.value ? {} : { projectId: null }) });
  if (act === "project") void save({ projectId: el.value || null });
  if (act === "active") void save({ active: el.checked });
  if (act === "dl") void save({ allowDownload: el.checked });
  if (act === "kind") void save({ kind: (el as unknown as HTMLSelectElement).value === "preview" ? "preview" : "final" });
  if (act === "sz") void save({ showSafeZone: el.checked });
  if (act === "expires") void save({ expiresAt: el.value ? new Date(`${el.value}T23:59:59`).toISOString() : null });
  if (act === "f") {
    const k = el.dataset.k;
    if (k === "title" && el.value.trim()) void save({ title: el.value.trim() }, false);
    if (k === "message") void save({ message: el.value }, false);
  }
  if (act === "pcap") {
    const v = d.videoRows.find((x) => x.id === el.dataset.id);
    const p = el.dataset.k as Platform;
    if (v) {
      if (el.value.trim()) v.captions[p] = el.value;
      else delete v.captions[p];
    }
    el.closest(".dv-cap")?.querySelector(`.dv-ctab[data-k="${p}"]`)?.classList.toggle("has", !!el.value.trim());
    void api.saveDeliveryVideo(d.id, el.dataset.id ?? "", { platformCaption: { platform: p, text: el.value } }).then(() => H.toast(`Text für ${PLATFORMS[p].label} gespeichert`), (err: unknown) => H.toast(failMsg(err)));
  }
  if (act === "caption") {
    const v = d.videoRows.find((x) => x.id === el.dataset.id);
    if (v) v.caption = el.value;
    el.closest(".dv-cap")?.querySelector('.dv-ctab[data-k="all"]')?.classList.toggle("has", !!el.value.trim());
    void api.saveDeliveryVideo(d.id, el.dataset.id ?? "", { caption: el.value }).then(() => H.toast("Beitragstext gespeichert"), (err: unknown) => H.toast(failMsg(err)));
  }
  if (act === "vtitle" && el.value.trim()) void api.saveDeliveryVideo(d.id, el.dataset.id ?? "", { title: el.value.trim() }).catch((err: unknown) => H.toast(failMsg(err)));
  if (el instanceof HTMLInputElement && el.type === "file") el.value = "";
});

document.addEventListener("submit", (e) => {
  const f = (e.target as HTMLElement).closest<HTMLFormElement>("[data-dform]");
  if (!f) return;
  e.preventDefault();
  const val = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";
  const btn = f.querySelector<HTMLButtonElement>("button[type=submit]");
  if (btn) btn.disabled = true;
  if (f.dataset.dform === "code") {
    const d = CUR?.d;
    if (!d) return;
    void (async () => {
      try {
        CUR!.d = (await api.deliveryCode(d.id, { code: val("dv-c-code") })).delivery;
        H.closeModal();
        paint();
        H.toast(`Code ${CUR!.d.code.slice(0, 3)} ${CUR!.d.code.slice(3)} gilt jetzt`);
      } catch (err) {
        if (!H.authLost(err)) H.toast(failMsg(err));
        if (btn) btn.disabled = false;
      }
    })();
    return;
  }
  void (async () => {
    try {
      const { delivery } = await api.createDelivery({
        title: val("dv-n-title"),
        message: val("dv-n-msg"),
        clientId: val("dv-n-client") || null,
        projectId: val("dv-n-project") || null,
        withCode: (document.getElementById("dv-n-code") as HTMLInputElement | null)?.checked !== false,
        kind: (document.querySelector<HTMLInputElement>('input[name="dv-n-kind"]:checked')?.value as DeliveryKind | undefined) ?? "final",
      });
      H.closeModal();
      LIST = undefined;
      CUR = { id: delivery.id, d: delivery, err: "" };
      H.go(`abgabe-${delivery.id}`);
    } catch (err) {
      if (!H.authLost(err)) H.toast(failMsg(err));
      if (btn) btn.disabled = false;
    }
  })();
});

/*
 * Datei auswählen: mit einem eigenen Feld ausserhalb der Seite. Die Abgabe wird im Hintergrund
 * neu geladen (Feedback, Übertragung); ein Feld in der Seite würde dabei ersetzt, und die Auswahl
 * nach längerem Suchen im Dateifenster ginge verloren.
 */
document.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>("[data-pick]");
  if (!b || !CUR?.d) return;
  e.preventDefault();
  const deliveryId = CUR.d.id;
  const replaceId = b.dataset.pick === "replace" ? (b.dataset.id ?? "") : "";
  // ausserhalb von #view, damit Neuzeichnen es nicht ersetzt (ältere Safari brauchen es im Dokument)
  document.getElementById("dv-pick")?.remove();
  const input = document.createElement("input");
  input.id = "dv-pick";
  input.hidden = true;
  input.type = "file";
  input.accept = CUR.d.kind === "photos" ? "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" : "video/*";
  input.multiple = !replaceId;
  input.addEventListener("change", () => {
    const files = [...(input.files ?? [])];
    input.remove();
    if (!files.length) return;
    void uploadFiles(deliveryId, replaceId ? [files[0]!] : files, replaceId || undefined);
  });
  document.body.appendChild(input);
  input.click();
});

// Videos per Drag & Drop
document.addEventListener("dragover", (e) => {
  const z = (e.target as HTMLElement).closest?.("#dv-drop");
  if (!z) return;
  e.preventDefault();
  z.classList.add("over");
});
document.addEventListener("dragleave", (e) => (e.target as HTMLElement).closest?.("#dv-drop")?.classList.remove("over"));
document.addEventListener("drop", (e) => {
  const z = (e.target as HTMLElement).closest?.("#dv-drop");
  if (!z || !CUR?.d) return;
  e.preventDefault();
  z.classList.remove("over");
  const photos = CUR.d.kind === "photos";
  const files = [...(e.dataTransfer?.files ?? [])].filter((f) => (photos ? f.type.startsWith("image/") || /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name) : f.type.startsWith("video/") || /\.(mp4|mov|m4v|webm)$/i.test(f.name)));
  if (!files.length) return H.toast(photos ? "Bitte Bilder (JPEG, PNG, WEBP, HEIC) ablegen." : "Bitte Videodateien (MP4, MOV, WEBM) ablegen.");
  void uploadFiles(CUR.d.id, files);
});

// Läuft eine Übertragung in die Nextcloud, alle 2.5 Sekunden nachsehen
setInterval(() => {
  if (!CUR?.d || !onDetail() || !CUR.d.videoRows.some((v) => v.status === "transfer")) return;
  void loadDetail(CUR.id);
}, 2500);

// Neues Feedback und Aktivität alle 30 Sekunden holen, solange die Abgabe offen ist
setInterval(() => {
  if (!CUR?.d || !onDetail() || document.visibilityState !== "visible") return;
  if (UPLOADS.some((u) => u.state === "up" || u.state === "prep")) return;
  if (document.activeElement?.closest(".dv-layout, .dv-head")) return;
  void loadDetail(CUR.id);
}, 30_000);

window.addEventListener("beforeunload", (e) => {
  if (UPLOADS.some((u) => u.state === "up" || u.state === "prep")) e.preventDefault();
});
